import isEqual from "lodash-es/isEqual"
import type { FeatureLike } from "ol/Feature"
import Feature from "ol/Feature"
import LineString from "ol/geom/LineString"
import VectorLayer from "ol/layer/Vector"
import type OlMap from "ol/Map"
import { fromLonLat } from "ol/proj"
import VectorSource from "ol/source/Vector"
import { Stroke, Style } from "ol/style"
import { useTheme } from "vuetify"

export const useOpenLayersRelations = (
  map: Ref<OlMap | null>,
  mainAsset: Ref<LookupAsset | undefined>,
  selectedFeatures: Ref<Array<FeatureLike>>,
  showMainAssetRelations: Ref<boolean>,
  assetShowRelations: Ref<boolean>,
) => {
  const authStore = useAuthStore()
  const assetStore = useAssetStore()

  const selectedRelatedAssets = ref<Array<LookupAsset>>([])
  const mainRelatedAssets = ref<Array<LookupAsset>>([])
  const fetchedAssetRelationsById = ref<Map<string, Array<LookupAsset>>>(new Map())

  const { removeLayer } = useLayerManagement(map)

  const theme = useTheme()

  const toggleRelationLayer = async () => {
    if (!map.value) return

    removeLayer(OpenLayersLayer.RELATION)

    if (!assetShowRelations.value) {
      return
    }

    const selectedAssetIds = selectedFeatures.value
      .filter((feature) => feature.get("type") === MarkerType.ASSET)
      .map((feature) => feature.get("id") as string)
      // Remove the main asset since the relations are already fetched
      .filter((assetId) => assetId !== mainAsset.value?._id)

    if (selectedAssetIds.length === 0 && mainAsset.value && showMainAssetRelations.value) {
      selectedAssetIds.push(mainAsset.value._id)
    }

    if (selectedAssetIds.length === 0) {
      return
    }

    const lineSource = new VectorSource()
    const lineStyle = new Style({
      stroke: new Stroke({
        color: parseColorToCSS(theme.current.value.colors.secondary),
        width: 2,
      }),
    })

    // Only if user has permission and an asset is selected
    for (const id of selectedAssetIds) {
      const relations = await assetStore.getRelationsByAssetId(id)
      if (!relations) {
        continue
      }

      for (const relation of relations) {
        if (!relation.asset1.location.gps?.coordinates || !relation.asset2.location.gps?.coordinates) {
          continue
        }

        const { lat: lat1, lng: lng1 } = geoPointToLatLng(relation.asset1.location.gps)
        const { lat: lat2, lng: lng2 } = geoPointToLatLng(relation.asset2.location.gps)
        const coordinates = [fromLonLat([lng1, lat1]), fromLonLat([lng2, lat2])]

        const geometry = new LineString(coordinates)
        const line = new Feature({ geometry })

        line.setStyle(lineStyle)
        lineSource.addFeature(line)
      }
    }

    const lineLayer = new VectorLayer({
      source: lineSource,
    })

    lineLayer.set("name", OpenLayersLayer.RELATION)
    map.value.addLayer(lineLayer)
  }
  // storage for already fetched relations

  const fetchRelatedSelectedFeaturesAssets = async () => {
    // Reset selected related assets in all account
    selectedRelatedAssets.value = []

    if (!assetShowRelations.value) {
      return
    }

    const selectedAssetIds = selectedFeatures.value
      .filter((feature) => feature.get("type") === MarkerType.ASSET)
      .map((feature) => feature.get("id") as string)
      // Remove the main asset since the relations are already fetched
      .filter((assetId) => assetId !== mainAsset.value?._id)

    const fetchedRelatedAssets = await Promise.all(selectedAssetIds.map((assetId) => getAssetRelationsById(assetId)))

    selectedRelatedAssets.value = fetchedRelatedAssets.flat()

    await nextTick()

    toggleRelationLayer()
  }

  const getAssetRelationsById = async (assetId: string): Promise<Array<LookupAsset>> => {
    const fetchedRelations = fetchedAssetRelationsById.value.get(assetId)

    if (fetchedRelations) {
      return fetchedRelations
    }

    const relations = await assetStore.getRelationsByAssetId(assetId)
    const relatedAssets = relations.map((relation) => (relation.asset1._id === assetId ? relation.asset2 : relation.asset1))

    // Save so they don't need to be fetched again
    fetchedAssetRelationsById.value.set(assetId, relatedAssets)

    return relatedAssets
  }

  watch(selectedFeatures, () => {
    fetchRelatedSelectedFeaturesAssets()
  })

  watch(
    [mainAsset, showMainAssetRelations],
    async ([newAsset], [oldAsset]) => {
      if (isEqual(newAsset, oldAsset)) return

      if (!mainAsset.value || !authStore.hasScope(mainAsset.value.organization, AuthScope.CAN_VIEW_ASSET_RELATIONS)) {
        return
      }

      if (showMainAssetRelations.value) {
        mainRelatedAssets.value = await getAssetRelationsById(mainAsset.value._id)
      }

      await nextTick()
      toggleRelationLayer()
    },
    { deep: true, immediate: true },
  )

  watch(assetShowRelations, () => toggleRelationLayer())

  return {
    selectedRelatedAssets,
    mainRelatedAssets,
  }
}
